import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  CardStatesEnum,
  CardTypesEnum,
  CustomerCardDetailVm,
  CustomerCardSummaryVm,
  CustomerCardTypesEnum,
  CustomerDetailVm,
} from '@/api';
import { AccountTypes } from '@/constants/AccountTypes';
import { MIN_CUSTOMER_AGE } from '@/config/config';
import { toDate } from '@/utils/dateTime';
import { differenceInYears, startOfDay } from 'date-fns';
import { supportsEWallet } from '@T-system/common/src/utils/cardType';
import { useSystemConfigurationStore } from '@/store/useSystemConfigurationStore.ts';
import { useAuthStore } from '@/store/useAuthStore.ts';

/**
 * Subset of CustomerDetailVm that's actually used by the user account page.
 * Allows storing lightweight customer data (e.g. from registeredCustomer)
 * without forcing a full getCustomer API call.
 */
export type SelectedCustomerData = Partial<CustomerDetailVm>;

/**
 * Card data may come either from the full /last-active-card endpoint
 * or the lightweight /last-active-card-summary endpoint.
 */
export type CardData = Partial<CustomerCardDetailVm> & Partial<CustomerCardSummaryVm>;

export const useUserAccountStore = defineStore('userAccount', () => {
  const systemConfigurationStore = useSystemConfigurationStore();
  const authStore = useAuthStore();

  const hasActiveOrProcessedCardApplication = ref(false);
  const cardDetailData = ref<CardData>({
    state: {},
    customer: {},
    electronicWallet: {},
    cardProfiles: [],
  } as unknown as CardData);
  const selectedCustomerData = ref<SelectedCustomerData>({});
  const selectedAccount = ref(AccountTypes.REGISTERED);
  const canVirtualCardApplicationBeCreated = ref(true);
  const cardLoaded = ref(false);
  const canRegisterPortableCardAllowed = ref(false);
  const nonPortableCardApplicationsAllowed = ref(true);
  const hasUnprocessedCardApplication = ref(false);
  const unprocessedProfileApplicationsCount = ref(0);

  const isCardActive = computed(() => {
    return cardDetailData.value?.state?.name === CardStatesEnum.ACTIVE;
  });

  const isCardTypeMobilePhone = computed(() => {
    return cardDetailData.value?.type?.name === CardTypesEnum.MOBILE_PHONE;
  });

  const isCardTypeBank = computed(() => {
    return cardDetailData.value?.type?.name === CardTypesEnum.BANK_CARD;
  });

  const isCardTypeWithoutStructure = computed(() => {
    return cardDetailData.value?.type?.name === CardTypesEnum.SMART_CARD_WITHOUT_STRUCTURE;
  });

  const isCardTypeWithStructure = computed(() => {
    return (
      cardDetailData.value?.type?.name === CardTypesEnum.SMART_CARD ||
      cardDetailData.value?.type?.name === CardTypesEnum.SMART_CARD_2
    );
  });

  const isCardTypeWithChip = computed(() => {
    return cardDetailData.value?.customerCardType === CustomerCardTypesEnum.PERSONAL;
  });

  const isCardTypePortable = computed(() => {
    return cardDetailData.value?.customerCardType === CustomerCardTypesEnum.PORTABLE;
  });

  const isCardTypeVirtual = computed(() => {
    return cardDetailData.value?.customerCardType === CustomerCardTypesEnum.VIRTUAL_CARD;
  });

  const isCustomerProfileApplicationAllowed = computed(() => {
    const type = cardDetailData.value?.type?.name;
    return !!(
      systemConfigurationStore.isCustomerProfileApplicationAllowed &&
      isCardActive.value &&
      type &&
      type != CardTypesEnum.SMART_CARD_2 &&
      authStore.accountIsPersonal
    );
  });

  const isCardExpired = computed(() => {
    return cardDetailData.value?.state?.name === CardStatesEnum.EXPIRED;
  });

  const cardNumber = computed(() => {
    return cardDetailData.value?.cardNumber;
  });

  const hasCardDetailData = computed(() => {
    return !!cardDetailData.value?.id;
  });

  /**
   * Returns true if customer's personal data is verified.
   * Compatible with both full card detail (uses customer.personalDataVerifiedDate)
   * and summary card data (uses pre-computed personalDataVerified flag).
   */
  const personalDataVerified = computed(() => {
    const data = cardDetailData.value;
    // Summary DTO carries a pre-computed flag
    if ('personalDataVerified' in data && data.personalDataVerified !== undefined) {
      return Boolean(data.personalDataVerified);
    }
    // Full DTO has the date nested under customer
    return Boolean((data as Partial<CustomerCardDetailVm>)?.customer?.personalDataVerifiedDate);
  });

  const isSelectedAccountRegistered = computed(() => {
    return selectedAccount.value === AccountTypes.REGISTERED;
  });

  const customerIsChild = computed(() => {
    const currentDate = toDate(new Date());
    const birthDay = selectedCustomerData.value.birthDay;
    if (!birthDay) {
      return false;
    }
    const customerYears = differenceInYears(startOfDay(currentDate), startOfDay(birthDay));
    return customerYears < MIN_CUSTOMER_AGE;
  });

  const isCardActiveAndSupportsEWallet = (enabledEWalletOnVirtualCard: boolean) => {
    return (
      isCardActive.value &&
      supportsEWallet(cardDetailData.value?.type?.name as CardTypesEnum, enabledEWalletOnVirtualCard)
    );
  };

  const setCardData = (payload: CardData) => {
    cardDetailData.value = payload;
  };

  const setSelectedCustomerData = (payload: SelectedCustomerData) => {
    selectedCustomerData.value = payload;
  };

  const setCardApplicationActiveOrProcessed = (activeOrProcessed: boolean) => {
    hasActiveOrProcessedCardApplication.value = activeOrProcessed;
  };

  const setSelectedAccount = (accountType: string) => {
    selectedAccount.value = accountType;
  };

  const clearSelectedCustomerData = () => {
    selectedCustomerData.value = {};
  };

  const setVirtualCardApplicationCanBeCreated = (canBeCreated: boolean) => {
    canVirtualCardApplicationBeCreated.value = canBeCreated;
  };

  const setCardLoaded = () => {
    cardLoaded.value = true;
  };

  const setPortableCardRegistration = (payload: { allowed: boolean; otherCardsApplicationsAllowed: boolean }) => {
    canRegisterPortableCardAllowed.value = payload.allowed;
    nonPortableCardApplicationsAllowed.value = payload.otherCardsApplicationsAllowed;
  };

  const setHasUnprocessedCardApplication = (value: boolean) => {
    hasUnprocessedCardApplication.value = value;
  };

  const setUnprocessedProfileApplicationsCount = (count: number) => {
    unprocessedProfileApplicationsCount.value = count;
  };

  const clearCardData = () => {
    cardDetailData.value = {
      state: {},
      customer: {},
      electronicWallet: {},
    } as CardData;
  };

  return {
    hasActiveOrProcessedCardApplication,
    cardDetailData,
    selectedCustomerData,
    selectedAccount,
    canVirtualCardApplicationBeCreated,
    isCardActive,
    isCardTypeMobilePhone,
    isCardTypeBank,
    isCardTypeWithoutStructure,
    isCardTypeWithStructure,
    isCardTypeWithChip,
    isCardTypePortable,
    isCardTypeVirtual,
    isCustomerProfileApplicationAllowed,
    isCardExpired,
    cardNumber,
    hasCardDetailData,
    personalDataVerified,
    isSelectedAccountRegistered,
    customerIsChild,
    cardLoaded,
    canRegisterPortableCardAllowed,
    nonPortableCardApplicationsAllowed,
    hasUnprocessedCardApplication,
    unprocessedProfileApplicationsCount,

    setUnprocessedProfileApplicationsCount,
    setPortableCardRegistration,
    setHasUnprocessedCardApplication,
    isCardActiveAndSupportsEWallet,
    setCardData,
    setSelectedCustomerData,
    setCardApplicationActiveOrProcessed,
    setSelectedAccount,
    clearSelectedCustomerData,
    setVirtualCardApplicationCanBeCreated,
    setCardLoaded,
    clearCardData,
  };
});
